import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    VictoryAxis,
    VictoryBoxPlot,
    VictoryBrushContainer,
    VictoryChart,
    VictoryLabel,
    VictoryLine,
    VictoryScatter,
    VictoryTheme,
    VictoryTooltip,
    VictoryZoomContainer,
} from 'victory'
import { DataContext } from './DataContext'
import { AppStateContext } from '../AppStateContext'
import Message from '../../../elem/chart/Message'
import Spinner from '../../../elem/Spinner'
import dummyData from './utility/dummyData'
import WrappedTick from './utility/WrappedTick'
import BoxPlotTooltip, {
    boxEvents,
    facilityTypeHasLessThan20Point,
    ScatterTooltip,
} from './utility/BoxPlotTooltip'
import { getColorByFacilityType } from '../../../../utils/chart/palette'
import { minBy, maxBy } from 'lodash'

const maxPoints = 500
const expandedStyles = {
    dotSize: 2,
    selectedDotSize: 5,
    tickMaxWidth: 180,
    axisStyles: {
        // these have to be supplied as svg values
        axisLabel: {
            fontSize: 16,
            fontWeight: 'bolder',
        },
        tickLabels: {
            fontSize: 16,
            lineHeight: 1,
            fontFamily:
                'BlinkMacSystemFont, -apple-system, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "Helvetica", "Arial", sans-serif;',
        },
    },
}

const normalStyles = {
    dotSize: 2,
    selectedDotSize: 5,
    tickMaxWidth: 80,
    axisStyles: {
        // these have to be supplied as svg values
        axisLabel: {
            fontSize: 11,
            fontWeight: 'bolder',
        },
        tickLabels: {
            fontSize: 11,
            lineHeight: 1,
            fontFamily:
                'BlinkMacSystemFont, -apple-system, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "Helvetica", "Arial", sans-serif;',
        },
    },
}

const boxPlotStyles = {
    // these have to be supplied as svg values
    boxes: {
        fill: 'black',
        opacity: 0.4,
    },
}

const BoxPlotChart = ({ staticHeightWidth, isExpanded }) => {
    const {
        mapState: { selectedFeatures },
    } = useContext(AppStateContext)

    const { boxData, scatterData, loading, filterMode } = useContext(
        DataContext
    )
    // const scatterData = dummyData.scatterData
    // const boxData = dummyData.boxData

    const plotStyles = isExpanded ? expandedStyles : normalStyles
    // const plotStyles = expandedStyles

    const allUniqueFacilityTypes = useMemo(
        () => [...new Set(scatterData.map(x => x.FacilityTypeDisplay))],
        [scatterData]
    )

    const allUniqueFacilityTypesWithCode = useMemo(
        () =>
            allUniqueFacilityTypes.map(x => ({
                FacilityTypeDisplay: x,
                FacilityType: scatterData.find(d => d.FacilityTypeDisplay === x)
                    .FacilityType,
            })),
        [allUniqueFacilityTypes, scatterData]
    )

    const boxChartData = useMemo(
        () =>
            boxData.map(d => ({
                x: d.FacilityTypeDisplay,
                fill: getColorByFacilityType(d.FacilityType).fill,
                ...d,
            })),
        [boxData]
    )

    const scatterChartData = useMemo(
        () =>
            scatterData.map(d => ({
                x: d.FacilityTypeDisplay,
                y: d.ResultValue,
                fill: getColorByFacilityType(d.FacilityType).fill,
                stroke: getColorByFacilityType(d.FacilityType).stroke,
                ...d,
            })),
        [scatterData]
    )

    const [initialDomain, setInitialDomain] = useState({
        y: [0, 0],
        x: [0, 0],
    })

    const [zoomState, setZoomState] = useState({
        zoomedYDomain: [0, 0],
        zoomedXDomain: [0, 0],
    })

    const adjustDomain = domain => {
        const [min, max] = domain
        return max - min < 1
            ? [Math.max(0, min - 1).toFixed(2), (max + 1).toFixed(2)]
            : domain
    }

    const yMin = useMemo(
        () =>
            (scatterChartData &&
                scatterChartData.length &&
                minBy(scatterChartData, d => d.y).y) ||
            0,
        [scatterChartData]
    )
    const yMax = useMemo(
        () =>
            (scatterChartData &&
                scatterChartData.length &&
                maxBy(scatterChartData, d => d.y).y) ||
            0,
        [scatterChartData]
    )

    useEffect(() => {
        let zoomCalc = { zoomedYDomain: [0, 0], zoomedXDomain: [0, 0] }

        if (!scatterChartData || !scatterChartData.length) {
            setZoomState(zoomCalc)
            setInitialDomain({
                y: zoomCalc.zoomedYDomain,
                x: zoomCalc.zoomedXDomain,
            })
            return
        }

        zoomCalc = {
            zoomedYDomain: adjustDomain([yMin, yMax]),
            zoomedXDomain: [1, allUniqueFacilityTypes.length],
        }

        setZoomState(zoomCalc)
        setInitialDomain({
            y: zoomCalc.zoomedYDomain,
            x: zoomCalc.zoomedXDomain,
        })
    }, [
        scatterChartData,
        setInitialDomain,
        setZoomState,
        allUniqueFacilityTypes,
    ])
    // const entireDomain = useMemo(() => {}, [scatterChartData])

    const onDomainChange = useCallback(
        (domain, targetAxis) => {
            const [lowX, highX] = domain.x
            const [lowY, highY] = domain.y

            const getZoomedYDomain = () => {
                if (targetAxis !== 'y') return null

                const minY = lowY <= 0 ? 0 : lowY
                const maxY = highY >= yMax ? yMax : highY
                return adjustDomain([minY, maxY])
            }

            const getZoomedXDomain = () => {
                if (targetAxis !== 'x') return null

                const minX = lowX <= 1 ? 1 : Math.ceil(lowX)
                const maxX =
                    Math.floor(highX) >= allUniqueFacilityTypes.length
                        ? allUniqueFacilityTypes.length
                        : Math.floor(highX)
                return [minX, maxX]
            }

            setZoomState(prev => ({
                zoomedYDomain: getZoomedYDomain() || prev.zoomedYDomain,
                zoomedXDomain: getZoomedXDomain() || prev.zoomedXDomain,
            }))
        },
        [yMax, allUniqueFacilityTypes.length]
    )

    // This callback function filters and downsamples scatter plot data based on zoom state
    // to maintain performance while preserving important data points
    const shrinkedScatterData = useMemo(() => {
        // Extract the current zoom domains from zoomState
        const { zoomedYDomain, zoomedXDomain } = zoomState

        // Filter data points to only include those within the current zoom domains:
        // - y-value must be within y-domain bounds
        // - x-value's index in allUniqueFacilityTypes must be within rounded x-domain bounds
        const filtered = scatterChartData.filter(
            d => d.y >= zoomedYDomain[0] && d.y <= zoomedYDomain[1]
        )
        // .filter(d => {
        //     const idx = allUniqueFacilityTypes.indexOf(d.x) + 1
        //     return idx >= zoomedXDomain[0] && idx <= zoomedXDomain[1]
        // })

        // Group points with equal y values
        const groupedData = filtered.reduce((acc, point) => {
            const key = `${point.x}-${point.y}`
            if (!acc[key]) {
                acc[key] = {
                    x: point.x,
                    y: point.y,
                    SampleCount: 0,
                    originalPoints: [],
                }
            }
            acc[key].SampleCount++
            acc[key].originalPoints.push(point)
            return acc
        }, {})

        // Convert grouped data back to array
        const groupedFiltered = Object.values(groupedData).map(group => ({
            ...group,
            // Preserve other original properties from the first point in the group
            ...group.originalPoints[0],
        }))

        // If the grouped filtered data exceeds the maximum allowed points (maxPoints), downsample it
        if (groupedFiltered.length > maxPoints) {
            // Calculate downsampling ratio (keep 1 point every k points)
            const k = Math.ceil(groupedFiltered.length / maxPoints)

            // Initial downsampling by taking every k-th point
            const maxPointFiltered = groupedFiltered.filter(
                (d, i) => i % k === 0
            )

            // Ensure Y-coverage for each facility type without overwriting existing logic
            allUniqueFacilityTypes.forEach(ft => {
                const facilityData = groupedFiltered.filter(d => d.x === ft)
                const existingPoints = maxPointFiltered.filter(d => d.x === ft)

                if (facilityData.length > 0) {
                    // Create Y buckets for this facility type
                    const yStep = 2
                    const yBuckets = {}

                    facilityData.forEach(d => {
                        const bucket = Math.floor(
                            (d.y - zoomedYDomain[0]) / yStep
                        )
                        if (!yBuckets[bucket]) {
                            yBuckets[bucket] = d // Keep first point in each bucket
                        }
                    })

                    // Get the bucket points we need to add
                    const bucketPoints = Object.values(yBuckets)

                    // Filter out points that are already in our maxPointFiltered data
                    const pointsToAdd = bucketPoints.filter(
                        bp =>
                            !existingPoints.some(
                                ep => ep.x === bp.x && ep.y === bp.y
                            )
                    )

                    // Add the necessary points to maintain Y coverage
                    maxPointFiltered.push(...pointsToAdd)
                }
            })

            // Ensure each facility type has at least 20 points visible
            allUniqueFacilityTypes.forEach(ft => {
                // Get points for this facility type in the downsampled data
                const facilityTypeSeparatedFilteredData = maxPointFiltered.filter(
                    d => d.x === ft
                )

                // If we have less than 20 points for this facility type
                if (facilityTypeSeparatedFilteredData.length <= 20) {
                    // Take up to 20 points from the original grouped data for this facility type
                    const facilityTypeSeparatedDomainData = groupedFiltered
                        .filter(d => d.x === ft)
                        .filter((d, i) => i < 20)

                    // Add these points to the downsampled data
                    maxPointFiltered.push(...facilityTypeSeparatedDomainData)
                }
            })

            // Ensure the maximum y-value in the current domain is always visible
            const maximumYValueInDomain = maxBy(groupedFiltered, d => d.y)
            if (maximumYValueInDomain) {
                maxPointFiltered.push(maximumYValueInDomain)
            }

            return maxPointFiltered
        }

        // If we're under the point limit, return all grouped filtered points
        return groupedFiltered
    }, [zoomState, scatterChartData, allUniqueFacilityTypes]) // Dependencies for useMemo

    const [xBrushOpacity, setXBrushOpacity] = useState(0.3)
    const [yBrushOpacity, setYBrushOpacity] = useState(0.3)

    return loading ? (
        <Spinner extraClass="chartWrapper" />
    ) : !selectedFeatures.length && filterMode === 'Selected Wells' ? (
        <Message
            text={'No Wells Selected'}
            subText={`Select wells with measurable Nitrate samples to generate boxplots`}
        />
    ) : selectedFeatures.length &&
      !(scatterChartData && scatterChartData.length) ? (
        <Message
            text={'No Nitrate Data Available'}
            subText={`Select wells with measurable Nitrate samples to generate boxplots`}
        />
    ) : (
        <>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                }}
            >
                <div>
                    <VictoryChart
                        domain={initialDomain}
                        domainPadding={{ x: [12, 12] }}
                        theme={VictoryTheme.material}
                        containerComponent={
                            <VictoryBrushContainer
                                // responsive={false}
                                brushDimension="y"
                                allowDraw={false}
                                onBrushDomainChange={domain => {
                                    onDomainChange(domain, 'y')
                                    setYBrushOpacity(0.6)
                                }}
                                onBrushDomainChangeEnd={domain => {
                                    setYBrushOpacity(0.3)
                                }}
                                brushStyle={{
                                    fill: '#fdaa48',
                                    fillOpacity: yBrushOpacity,
                                    stroke: '#fdaa48',
                                }}
                            />
                        }
                        // {...staticHeightWidth}
                        width={25}
                    >
                        <VictoryAxis
                            dependentAxis //Y axis
                            style={{
                                axis: { stroke: 'black' },
                                ticks: { stroke: 'none' },
                                tickLabels: { fill: 'none' },
                            }}
                        />
                        <VictoryAxis
                            dependentAxis //Y axis
                            invertAxis
                            style={{
                                axis: { stroke: 'black' },
                                ticks: { stroke: 'none' },
                                tickLabels: { fill: 'none' },
                            }}
                        />
                    </VictoryChart>
                </div>
                <div>
                    <VictoryChart
                        padding={{
                            top: 20,
                            bottom: 100,
                            left: isExpanded ? 150 : 100,
                            right: 100,
                        }}
                        domain={{
                            x: zoomState.zoomedXDomain,
                            y: zoomState.zoomedYDomain,
                        }} //{{ x: initialDomain.x, y: zoomState.zoomedYDomain }}
                        domainPadding={{ x: [12, 12] }}
                        theme={VictoryTheme.material}
                        containerComponent={
                            <VictoryZoomContainer
                                allowPan={false}
                                allowZoom={false}
                                responsive={false}
                                // onZoomDomainChange={onDomainChange}
                            />
                        }
                        {...staticHeightWidth}
                    >
                        <VictoryAxis
                            crossAxis //X axis
                            tickLabelComponent={
                                <WrappedTick
                                    maxWidth={plotStyles.tickMaxWidth}
                                    facilityTypes={
                                        allUniqueFacilityTypesWithCode
                                    }
                                />
                            }
                            label={'Well Type'}
                            axisLabelComponent={<VictoryLabel dy={62} />}
                            style={plotStyles.axisStyles}
                        />
                        <VictoryAxis
                            dependentAxis //Y axis
                            tickFormat={(tick, index, ticks) => {
                                // Handle non-numeric values and edge cases
                                if (typeof tick !== 'number' || isNaN(tick)) {
                                    return '0.00'
                                }

                                // Round to 2 decimal places
                                const rounded =
                                    Math.round((tick + Number.EPSILON) * 100) /
                                    100

                                // Ensure exactly 2 decimal places (even for whole numbers)
                                return rounded
                            }}
                            label={'Concentration (mg/l)'}
                            axisLabelComponent={<VictoryLabel dy={-50} />}
                            style={plotStyles.axisStyles}
                        />
                        <VictoryLine
                            y={d => 10}
                            style={{
                                data: {
                                    stroke: 'red',
                                    strokeDasharray: '3 3',
                                    strokeWidth: '0.7px',
                                },
                            }}
                        />
                        <VictoryBoxPlot
                            boxWidth={20}
                            data={boxChartData}
                            q1Labels={() => ''}
                            q1LabelComponent={
                                <BoxPlotTooltip
                                    dimensions={staticHeightWidth}
                                    scatterChartData={scatterChartData}
                                />
                            }
                            q3Labels={() => ''}
                            q3LabelComponent={
                                <BoxPlotTooltip
                                    dimensions={staticHeightWidth}
                                    scatterChartData={scatterChartData}
                                />
                            }
                            medianLabels={() => ''}
                            medianLabelComponent={
                                <BoxPlotTooltip
                                    dimensions={staticHeightWidth}
                                    scatterChartData={scatterChartData}
                                />
                            }
                            minLabels={() => ''}
                            minLabelComponent={
                                <BoxPlotTooltip
                                    dimensions={staticHeightWidth}
                                    scatterChartData={scatterChartData}
                                />
                            }
                            maxLabels={() => ''}
                            maxLabelComponent={
                                <BoxPlotTooltip
                                    dimensions={staticHeightWidth}
                                    scatterChartData={scatterChartData}
                                />
                            }
                            style={{
                                whiskers: {
                                    visibility: ({ datum }) =>
                                        facilityTypeHasLessThan20Point(scatterChartData, datum)
                                            ? 'hidden'
                                            : 'visible',
                                },
                                boxes: {
                                    ...boxPlotStyles.boxes,
                                    visibility: ({ datum }) =>
                                        facilityTypeHasLessThan20Point(scatterChartData, datum)
                                            ? 'hidden'
                                            : 'visible',
                                },
                            }}
                            events={[...boxEvents]}
                        />
                        <VictoryScatter
                            size={3}
                            data={shrinkedScatterData}
                            labels={''} //data point labels, the VictoryTooltip will not display if this prop is unset, I believe current version doesn't have this issue
                            labelComponent={
                                <VictoryTooltip
                                    // pointerOrientation="left"
                                    dx={10}
                                    dy={-100}
                                    flyoutComponent={
                                        <ScatterTooltip
                                            isExpanded={isExpanded}
                                            dimensions={staticHeightWidth}
                                        />
                                    }
                                />
                            }
                            style={{
                                data: {
                                    fill: ({ datum }) => datum.fill,
                                    stroke: ({ datum }) => datum.stroke,
                                    strokeWidth: 1,
                                },
                            }}
                            events={[
                                {
                                    target: 'data',
                                    eventHandlers: {
                                        onMouseOver: () => {
                                            return [
                                                {
                                                    target: 'data',
                                                    mutation: props => {
                                                        return { size: 6 }
                                                    },
                                                },
                                                {
                                                    target: 'labels',
                                                    mutation: props => ({
                                                        active: true,
                                                    }),
                                                },
                                            ]
                                        },
                                        onMouseOut: () => {
                                            return [
                                                {
                                                    target: 'data',
                                                    mutation: props => ({
                                                        size: 3,
                                                    }),
                                                },
                                                {
                                                    target: 'labels',
                                                    mutation: props => ({
                                                        active: false,
                                                    }),
                                                },
                                            ]
                                        },
                                    },
                                },
                            ]}
                        />
                    </VictoryChart>
                </div>
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: `20px 0 ${(isExpanded && `20px 65px`) || ''}`,
                }}
            >
                <VictoryChart
                    domain={initialDomain}
                    theme={VictoryTheme.material}
                    // padding={{ top: 0, left: 10, right: 10, bottom: 0 }}
                    containerComponent={
                        <VictoryBrushContainer
                            responsive={false}
                            brushDimension="x"
                            allowDraw={false}
                            onBrushDomainChange={domain => {
                                onDomainChange(domain, 'x')
                                setXBrushOpacity(0.6)
                            }}
                            onBrushDomainChangeEnd={domain => {
                                setXBrushOpacity(0.3)
                            }}
                            brushStyle={{
                                fill: '#fdaa48',
                                fillOpacity: xBrushOpacity,
                                stroke: '#fdaa48',
                            }}
                        />
                    }
                    // {...staticHeightWidth}
                    height={25}
                >
                    <VictoryAxis
                        crossAxis //X axis
                        style={{
                            axis: { stroke: 'none' },
                            ticks: { stroke: 'none' },
                            tickLabels: { fill: 'none' },
                        }}
                    />
                </VictoryChart>
            </div>
        </>
    )
}

export default BoxPlotChart
